/**
 * RegistrationGateModal - Prompts anonymous users to register
 * 
 * Shown when anonymous users attempt actions that require registration:
 * - Playing any book other than promotional books (is_promotional=true)
 * - Using Ask Audia features
 * - Adding books to library
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, UserPlus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';

export default function RegistrationGateModal() {
  const navigate = useNavigate();
  const { showRegistrationModal, setShowRegistrationModal, gateContext } = useReaderFunnel();
  
  if (!showRegistrationModal) return null;
  
  const handleCreateAccount = () => {
    setShowRegistrationModal(false);
    navigate('/login', { state: { mode: 'register', returnTo: gateContext } });
  };
  
  const handleDismiss = () => {
    setShowRegistrationModal(false);
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleDismiss()}
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 mx-auto mb-3 rounded-lg"
          />
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-3 bg-purple-600/20 rounded-xl">
              <Sparkles className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">Continue Your NewAudia Journey</h2>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            Register a <span className="text-purple-400 font-semibold">FREE account</span> to unlock full access. 
            You'll receive <span className="text-yellow-400 font-semibold">10 complimentary Reader Credits</span> to 
            continue exploring NewAudia.
          </p>
          
          {/* Benefits list */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-slate-300">Access to full library</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-slate-300">Ask Audia AI companion</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-slate-300">Save your progress</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <span className="text-sm text-slate-300">10 Free Reader Credits</span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleCreateAccount}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create Free Account
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
